/**
 * One account's statement — the account's Bills (docs/tally/20 §6–§7; the
 * statement, 2026-10; Customer 360 Phase A, 02 §5.5): what TallyPrime says is
 * open on the account, laid out as a customer reads a statement.
 *
 *   - the balance and how it is made up — the open bills, what is owed
 *     against no bill, less the credits — adding up to TallyPrime's balance
 *     to the paisa;
 *   - the decisive split first: what is overdue (past an actual due date,
 *     grace included, the grace said apart) beside what is not yet due, by due
 *     date — or, switched, how old the money is, by age. An age is never called
 *     overdue, and critical age colours nothing here (02 §19.9 C1);
 *   - the open bills as a table, oldest first, each with its own dates, how it
 *     stands and its age; long lists show their first bills and say how many
 *     more;
 *   - the credits, with Timely allocations for collections labelled as such,
 *     never as TallyPrime's;
 *   - Cleared: the bills TallyPrime no longer lists as open. Receipts are not
 *     read from TallyPrime (owner decision, 02 §19.5), so how a bill was paid is
 *     not known and nothing here says it was a receipt. What else Timely saw
 *     change in TallyPrime, with how far TallyPrime says where the money went,
 *     is for people who may read the receivables data only (01 §6.1, tier 2).
 *
 * A bill with no due date counts as overdue only where the company chose to
 * read it as due on its bill date (02 F3): `useAccountStatement` reads the
 * account with that rule, so the band, Bills and the printed statement give the
 * same overdue figure.
 *
 * The same statement prints for the customer (`CustomerStatement`) and
 * downloads for a spreadsheet (`statementCsv`), both for someone who may export
 * (`statementAccess.ts`). Shown only for an account that comes from TallyPrime
 * and only once the database stores what TallyPrime said (0036); otherwise
 * nothing. An account from a sheet has no bills; its statement is its balance
 * and its ageing (`SheetStatement`).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    AgeTag, AgeingBar, Badge, Button, DialogShell, Panel, RowHead, SegmentBar, Segmented, SkeletonRows,
    Table, Tabs, Td, Th, ageSwatch, buttonClass, cx, errorClass, fieldClass, helpClass, labelClass, lateSwatch, type Tone,
} from '@timely/ui';
import {
    ALLOCATION_STATES, CLOSED_BILL_MEMORY_DAYS, EXPLANATIONS, LATENESS_LABELS, OPERATIONAL_STATE_WORDS, STATEMENT_LINE_WORDS,
    accountStatement, billsOverdue, bucketLabels, collectionView, holds, interpretReceivables, noDueDateRule, overdueSettings,
    standingWords, unallocatedCreditPool, validateOperationalAllocation,
    type AccountPosition, type AccountStatement as StatementFigures, type AllocationExplanation, type AllocationStatus,
    type BillStanding, type CreditEvent, type NoDueDateRule, type OperationalAllocation, type OperationalStateReason,
    type ReceivablesSettings, type StatementCredit,
} from '@timely/domain';
import { useFormatters } from '../book/useFormatters';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { paths } from '../workspace/navigation';
import { exportStatementCsv, mayExportStatement } from './statementAccess';
import { useReceivablesSettings } from './useReceivablesSettings';
import { createAllocation, useAccountDetail, withdrawAllocation, type AccountDetail, type StoredAllocation, type StoredEvent } from './useSourceFacts';

const STATUS_TONE: Record<AllocationStatus, Tone> = {
    EXACT_FROM_TALLY: 'pos', DERIVED_WITH_HIGH_CONFIDENCE: 'neutral', UNALLOCATED_FROM_TALLY: 'warn', AMBIGUOUS: 'warn',
};

/** How many open bills a long statement shows before "Show all". */
export const FIRST_BILLS = 12;

/** The account as the domain reads it, with the company's settings. */
export function positionOf(detail: AccountDetail, settings: ReceivablesSettings): AccountPosition | null {
    const f = detail.facts;
    if (!f) return null;
    const p = interpretReceivables({
        asOf: f.asOf, unmatchedBills: [],
        accounts: [{
            sourceKey: f.sourceKey, ledgerGuid: f.sourceKey.replace(/^tally:/, ''), name: '', group: f.group, billWise: f.billWise,
            closing: Number(f.closing), unbilled: Number(f.unbilled), bills: f.bills.map(b => ({ ...b, pending: Number(b.pending) })),
            unbilledReason: Number(f.unbilled) === 0 ? null : f.billWise ? 'on_account' : 'not_bill_wise',
        }],
    }, settings);
    return p.accounts[0] ?? null;
}

/**
 * One account's statement and what it is built from: the stored facts, the
 * account as the company's settings read it — a bill with no due date counted
 * as overdue only where the company chose that (`noDueDateRule`) — and the
 * statement itself. The same for the account's page and the printed statement.
 */
export function useAccountStatement(workspaceId: string, customerId: string) {
    const detail = useAccountDetail(workspaceId, customerId);
    const settings = useReceivablesSettings(workspaceId);
    const d = detail.data;
    const rule: NoDueDateRule = noDueDateRule(settings.current);
    const read = useMemo(() => overdueSettings(settings.current.settings, rule), [settings.current.settings, rule]);
    const account = useMemo(() => (d ? positionOf(d, read) : null), [d, read]);
    const statement = useMemo(
        () => (account && d?.facts ? accountStatement(account, d.facts.closedBills ?? [], d.facts.asOf) : null),
        [account, d],
    );
    return {
        detail, account, statement, settings: read, rule,
        loading: detail.loading || settings.loading,
        available: detail.available,
        /** The company's own settings are read: reasons may be stated against its thresholds (never the standard ones by default). */
        settingsReady: !settings.loading && !settings.error,
    };
}

export type AccountStatementState = ReturnType<typeof useAccountStatement>;

const asOperational = (a: StoredAllocation, sourceKey: string): OperationalAllocation => ({
    id: a.id, sourceKey, creditEventId: a.creditEventId, lines: a.lines, reason: a.reason, createdBy: a.createdBy ?? '', createdAt: a.createdAt,
    state: a.state, ...(a.stateReason ? { stateReason: a.stateReason as OperationalStateReason } : {}),
});

type View = 'open' | 'credits' | 'cleared';
type Lens = 'due' | 'age';

export function AccountStatement({ state, customerId, customerName, statementTo, onChanged }: {
    /** The account's statement, read once for the whole page (`useAccountStatement`). */
    state: AccountStatementState;
    customerId: string;
    /** For the spreadsheet's file name. */
    customerName: string;
    /** Where the printable statement opens, for someone who may export. */
    statementTo: string;
    onChanged?: (message: string) => void;
}) {
    const { money, date } = useFormatters();
    const context = useActiveWorkspace();
    const workspaceId = context.workspace.id;
    const canExport = mayExportStatement(context);
    // What else Timely saw change in TallyPrime, with how far TallyPrime says where it went, is a diagnostic: only for
    // people who may read the receivables data (01 §6.1, tier 2).
    const canReadDiagnostics = holds(context, 'datasource.view');
    const { detail, account, statement, settings, rule, loading, available } = state;
    // Null until somebody chooses: then the first view with something in it.
    const [view, setView] = useState<View | null>(null);
    const [lens, setLens] = useState<Lens>('due');
    const [allBills, setAllBills] = useState(false);
    const [allocating, setAllocating] = useState<StoredEvent | 'general' | null>(null);
    const [withdrawing, setWithdrawing] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const d = detail.data;

    if (!available) return null;
    const title = 'Outstanding statement';
    if (loading || !d) {
        return (
            <Panel id="account-statement" headingLevel={3} title={title} description="Bills from TallyPrime.">
                <div aria-busy="true" aria-label="Loading the statement"><SkeletonRows rows={4} /></div>
            </Panel>
        );
    }
    if (!d.facts || !account || !statement) {
        return (
            <Panel id="account-statement" headingLevel={3} title={title} description="Bills from TallyPrime.">
                <p className="px-5 pb-5 text-secondary text-label-3">TallyPrime’s bills appear here after the next update from TallyPrime.</p>
            </Panel>
        );
    }

    const facts = d.facts;
    const active = d.allocations.filter(a => a.state === 'active').map(a => asOperational(a, facts.sourceKey));
    const viewOf = collectionView(account, active);
    const covered = new Map(viewOf.bills.map(b => [b.key, b.coveredForCollections]));
    const pool = unallocatedCreditPool(account);
    const canAllocate = d.canAllocate && account.billWise && pool - viewOf.allocatedByTimely > 0.004 && viewOf.bills.some(b => b.toCollect > 0);
    const s = statement;
    const overdue = billsOverdue(s, rule, settings.graceDays);
    const events = canReadDiagnostics ? d.events : [];

    const withdraw = async (id: string) => {
        setWithdrawing(id); setError(null);
        try { await withdrawAllocation(workspaceId, id, null); await detail.reload(); onChanged?.('Timely allocation withdrawn. It is kept for the record.'); }
        catch (e) { setError(e instanceof Error ? e.message : 'That could not be withdrawn just now.'); }
        finally { setWithdrawing(null); }
    };
    // The printable statement and the spreadsheet leave Timely as files: only for someone who may export.
    const exports = canExport ? (
        <>
            <Link to={statementTo} className={buttonClass({ variant: 'secondary', size: 'sm' })}>Statement PDF</Link>
            <Button size="sm" variant="secondary" onClick={() => exportStatementCsv(context, s, customerName)}>Excel (CSV)</Button>
        </>
    ) : undefined;

    // A ledger kept without bills has no bills, credits or closed bills to list: its one line says it all. Three views
    // at most, so they fit a phone's width without a hidden fourth.
    const cleared = s.billWise ? s.cleared : [];
    const views: { value: View; label: string; count?: number }[] = [
        ...(s.billWise ? [{ value: 'open' as const, label: 'Open bills', count: s.bills.length }] : []),
        ...(s.billWise && (s.credits.length > 0 || d.allocations.length > 0) ? [{ value: 'credits' as const, label: 'Credits', count: s.credits.length }] : []),
        ...(cleared.length > 0 || events.length > 0 ? [{ value: 'cleared' as const, label: 'Cleared', ...(cleared.length > 0 ? { count: cleared.length } : {}) }] : []),
    ];
    const first: View | undefined = s.bills.length > 0 ? 'open' : views.find(v => v.value !== 'open')?.value ?? views[0]?.value;
    const shown: View | undefined = view && views.some(v => v.value === view) ? view : first;
    const bills = allBills ? s.bills : s.bills.slice(0, FIRST_BILLS);
    const side = s.totals.balance < 0 ? 'Cr' : 'Dr';
    // Ageing by due date: the company's windows already count days past the due date — they are the overdue, and
    // Bills shows that one view (02 §19.9 C4). By bill date, the switch shows the due-date split or the age.
    const byDueDate = settings.ageing.basis === 'due_date';
    const shownLens: Lens = byDueDate ? 'due' : lens;

    return (
        <Panel
            id="account-statement" headingLevel={3} title={title}
            description={<>Bills from TallyPrime as on {date.day(s.asOf)} · read-only: nothing here changes TallyPrime.</>}
            actions={exports}
        >
            {!s.billWise ? (
                <p className="px-5 pb-4 text-secondary text-label-2">
                    TallyPrime keeps this ledger without bills:{' '}
                    <span className="num font-semibold text-label">{money.precise(Math.abs(s.totals.balance))} {side}</span>, not aged.
                    Without bills, Timely can’t say what is overdue.
                </p>
            ) : (
                <>
                    {/* No tiles of the balance and the overdue here: the band above every tab already says both, and this
                        is the statement's own document (Customer 360, 02 §5.5). Its totals close it, to the paisa. */}
                    {s.bills.length > 0 && (
                        <div className="px-5 pt-1 pb-4">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                {/* What the view shows, in words; the switch beside it names the two views. */}
                                <h4 className="text-secondary font-semibold text-label">{shownLens === 'due' ? 'How late the overdue is' : 'How old the bills are'}</h4>
                                {!byDueDate && (
                                    <Segmented label="Show the bills" value={lens} onChange={setLens}
                                        options={[{ value: 'due', label: 'By due date' }, { value: 'age', label: 'By age' }] as const} />
                                )}
                            </div>
                            {shownLens === 'due'
                                ? <DueSplit statement={s} account={account} overdue={overdue} settings={settings} byDueDate={byDueDate} />
                                : <AgeSplit account={account} settings={settings} />}
                        </div>
                    )}
                </>
            )}

            {shown && <Tabs label="What the statement shows" value={shown} onChange={setView} listClassName="px-3" tabs={views}>
                {shown === 'open' && (
                    <OpenBills
                        statement={s} bills={bills} covered={covered}
                        more={s.bills.length > FIRST_BILLS ? { all: allBills, toggle: () => setAllBills(!allBills) } : null}
                    />
                )}
                {shown === 'credits' && (
                    <div>
                        {s.credits.length > 0 ? <Credits credits={s.credits} total={s.totals.credits} />
                            : <p className="px-5 py-4 text-secondary text-label-3">TallyPrime holds no credit on this account now.</p>}
                        {d.allocations.length > 0 && (
                            <div className="px-5 pt-4 pb-2 border-t border-separator">
                                <h4 className="text-caption font-semibold text-label-3">{ALLOCATION_STATES.TIMELY_OPERATIONAL_ALLOCATION.label}</h4>
                                <ul aria-label="Timely allocations for collections" className="mt-1 divide-y divide-separator">
                                    {d.allocations.map(a => (
                                        <li key={a.id} className="py-2">
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="text-secondary text-label-2">{a.lines.map(l => `${l.ref} ${money.exact(l.amount)}`).join(', ')}</span>
                                                <Badge tone={a.state === 'active' ? 'brand' : a.state === 'suspended' ? 'warn' : 'neutral'}>
                                                    {a.state === 'active' ? 'In use' : a.state === 'suspended' ? 'Needs review' : a.state === 'superseded' ? 'TallyPrime decided' : 'Withdrawn'}
                                                </Badge>
                                            </div>
                                            <p className="text-caption text-label-3">{a.createdBy ?? 'Someone'}, {date.day(a.createdAt)}: “{a.reason}”</p>
                                            {a.stateReason && a.state !== 'withdrawn' && <p className="text-caption text-label-3">{OPERATIONAL_STATE_WORDS[a.stateReason as OperationalStateReason]}</p>}
                                            {a.comparison && (
                                                <p className="text-caption text-label-2">
                                                    TallyPrime: {a.comparison.tally.map(l => `${l.ref} ${money.exact(l.amount)}`).join(', ')}
                                                    {a.comparison.sameAsTimely ? ' — the same as Timely had.' : ' — not what Timely had.'}
                                                </p>
                                            )}
                                            {d.canAllocate && (a.state === 'active' || a.state === 'suspended') && (
                                                <Button size="sm" variant="quiet" busy={withdrawing === a.id} onClick={() => void withdraw(a.id)}>Withdraw</Button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {canAllocate && (
                            <div className="px-5 pt-3 pb-5 border-t border-separator">
                                <Button size="sm" variant="secondary" onClick={() => setAllocating('general')}>Allocate for collections</Button>
                                <p className={helpClass}>{money.precise(pool - viewOf.allocatedByTimely)} is held against no bill in TallyPrime.</p>
                            </div>
                        )}
                    </div>
                )}
                {shown === 'cleared' && (
                    <div>
                        <Cleared cleared={cleared} />
                        {events.length > 0 && (
                            <section aria-labelledby="statement-changes" className={cx(cleared.length > 0 && 'mt-2 border-t border-separator')}>
                                <h4 id="statement-changes" className="px-5 pt-4 text-secondary font-semibold text-label">What else Timely saw change in TallyPrime</h4>
                                <p className="px-5 pt-0.5 pb-1 text-caption text-label-3 max-w-3xl">
                                    Amounts falling on bills, credits applied, and bills pending again, each with how far TallyPrime itself says where the money
                                    went. Shown to people who may read the receivables data.
                                </p>
                                <ul aria-label="Changes seen in TallyPrime" className="divide-y divide-separator">
                                    {events.map(e => <EventRow key={e.eventKey} event={e} canAllocate={canAllocate} onAllocate={() => setAllocating(e)} />)}
                                </ul>
                            </section>
                        )}
                    </div>
                )}
            </Tabs>}
            {error && <p role="alert" className={cx(errorClass, 'px-5 pb-4')}>{error}</p>}

            {allocating && (
                <AllocationDialog
                    workspaceId={workspaceId} customerId={customerId} account={account} existing={active}
                    event={allocating === 'general' ? null : allocating}
                    onClose={() => setAllocating(null)}
                    onSaved={async () => { setAllocating(null); await detail.reload(); onChanged?.('Timely allocation recorded, for collections. TallyPrime is unchanged.'); }}
                />
            )}
        </Panel>
    );
}

/** One part of a split, as its legend shows it: a square of its colour, its words and its exact figure. */
const Key = ({ swatch, label, amount }: { swatch: string; label: string; amount: string | null }) => (
    <div className="min-w-0">
        <dt className="flex items-center gap-1.5 text-secondary text-label-2">
            <span aria-hidden="true" className={cx('size-2 flex-none', swatch)} />{label}
        </dt>
        <dd className={cx('mt-0.5 text-secondary font-semibold num', amount ? 'text-label' : 'text-label-3')}>{amount ?? '—'}</dd>
    </div>
);

/**
 * By due date: what is not yet due beside the overdue, and how late the overdue is — in the lateness ramp, never the
 * age ramp. A company that ages by due date sees its own windows here, because they count days past the due date.
 */
function DueSplit({ statement: s, account, overdue, settings, byDueDate }: {
    statement: StatementFigures;
    account: AccountPosition;
    overdue: ReturnType<typeof billsOverdue>;
    settings: ReceivablesSettings;
    byDueDate: boolean;
}) {
    const { money } = useFormatters();
    const context = useActiveWorkspace();
    const late = byDueDate
        ? bucketLabels(settings.ageing).map((label, i) => ({
            label, amount: account.buckets[i] ?? 0,
            fill: lateSwatch(account.buckets.length <= 1 ? 0 : Math.round((i * 3) / (account.buckets.length - 1))),
        }))
        : LATENESS_LABELS.map((label, i) => ({ label, amount: overdue.lateness[i]?.amount ?? 0, fill: lateSwatch(i) }));
    const parts = [{ label: 'Not yet due', amount: byDueDate ? account.notYetDue : overdue.notYetDue.amount, fill: 'bg-due-later' }, ...late];
    const said = parts.map(p => `${p.label} ${money.exact(p.amount)}`).join(', ');
    const noDue = overdue.noDueDate;
    return (
        <>
            <SegmentBar className="mt-3" label={`By due date: ${said}`} parts={parts.map(p => ({ amount: p.amount, fill: p.fill, key: p.label }))} />
            <dl aria-label="By due date" className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-x-6 gap-y-3">
                {parts.map(p => <Key key={p.label} swatch={p.fill} label={p.label} amount={p.amount > 0 ? money.precise(p.amount) : null} />)}
            </dl>
            <p className="mt-3 text-caption text-label-3 max-w-3xl">
                From TallyPrime’s due dates.{' '}
                {noDue.amount > 0 && noDue.rule === 'not_chosen' && <>
                    {money.precise(noDue.amount)} on {noDue.bills === 1 ? 'a bill' : 'bills'} with no due date {noDue.bills === 1 ? 'is' : 'are'} not counted as overdue: your settings choose no rule for them.{' '}
                    {holds(context, 'datasource.manage') && (
                        <Link to={paths(context.workspace.slug).setting('data')} className="font-semibold text-accent-text hover:underline">Choose one in Receivables data</Link>
                    )}
                </>}
                {noDue.amount > 0 && noDue.rule === 'not_aged' && <>{money.precise(noDue.amount)} on bills with no due date is left out, as your settings say.</>}
                {overdue.overdue.fromBillDate > 0 && <>It includes {money.precise(overdue.overdue.fromBillDate)} on bills with no due date, counted from the bill date, as your settings say.</>}
                {s.unbilledDebit && <> {money.precise(s.unbilledDebit.amount)} is owed against no bill, so it has no due date.</>}
            </p>
        </>
    );
}

/** By age: the company's own windows, in the navy age ramp. An age is time since the bill date, never overdue. */
function AgeSplit({ account, settings }: { account: AccountPosition; settings: ReceivablesSettings }) {
    const { money } = useFormatters();
    const labels = bucketLabels(settings.ageing);
    const rows = labels.map((label, i) => ({ label, amount: account.buckets[i] ?? 0 }));
    return (
        <>
            <AgeingBar className="mt-3 !h-2.5" parts={rows.map(r => r.amount)} label={`By age: ${rows.map(r => `${r.label} ${money.exact(r.amount)}`).join(', ')}`} />
            <dl aria-label="By age" className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-x-6 gap-y-3">
                {rows.map((r, i) => <Key key={r.label} swatch={ageSwatch(i, rows.length)} label={r.label} amount={r.amount > 0 ? money.precise(r.amount) : null} />)}
                {account.unagedNoDueDate > 0 && <Key swatch="bg-card-3" label="Not in any window" amount={money.precise(account.unagedNoDueDate)} />}
            </dl>
            <p className="mt-3 text-caption text-label-3 max-w-3xl">Time since the bill date, in your windows: an age, not overdue.</p>
        </>
    );
}

/** How one bill stands, in words. Past its due date is overdue, in crimson and in words, whatever the grace (rule 3). */
function Standing({ standing }: { standing: BillStanding }) {
    const tone = standing.kind === 'overdue' || standing.kind === 'grace' ? 'text-dang font-semibold'
        : standing.kind === 'due_today' ? 'text-warn' : 'text-label-3';
    return <span className={tone}>{standingWords(standing)}</span>;
}

function OpenBills({ statement: s, bills, covered, more }: {
    statement: StatementFigures;
    bills: StatementFigures['bills'];
    covered: Map<string, number>;
    more: { all: boolean; toggle: () => void } | null;
}) {
    const { money, date, count } = useFormatters();
    const side = s.totals.balance < 0 ? 'Cr' : 'Dr';
    if (s.bills.length === 0 && !s.unbilledDebit && s.credits.length === 0) {
        return <p className="px-5 py-4 text-secondary text-label-3">No open bills.</p>;
    }
    return (
        <div>
            {s.bills.length === 0 ? <p className="px-5 py-4 text-secondary text-label-3">No open bills.</p> : (
                <Table caption="Open bills, oldest first" aria-label="Open bills">
                    <thead>
                        <tr>
                            <Th>Bill</Th>
                            <Th hideBelow="sm">Bill date</Th>
                            <Th hideBelow="md">Due date</Th>
                            <Th hideBelow="sm">How it stands</Th>
                            <Th hideBelow="lg">Age</Th>
                            <Th numeric>Pending</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {bills.map(b => (
                            <tr key={b.key}>
                                <RowHead>
                                    <span className="text-secondary font-semibold text-label break-words">{b.ref || 'No reference'}</span>
                                    {/* A phone shows two columns: the bill's dates and standing go under its reference. */}
                                    <span className="block @min-[36rem]:hidden text-caption font-normal text-label-3">
                                        {date.day(b.billDate)} · <Standing standing={b.standing} />
                                    </span>
                                </RowHead>
                                <Td hideBelow="sm" className="text-secondary text-label-2 num whitespace-nowrap">{date.day(b.billDate)}</Td>
                                <Td hideBelow="md" className="text-secondary text-label-2 num whitespace-nowrap">{b.dueDate ? date.day(b.dueDate) : <span className="text-label-3">None given</span>}</Td>
                                <Td hideBelow="sm" className="text-secondary whitespace-nowrap"><Standing standing={b.standing} /></Td>
                                <Td hideBelow="lg">
                                    {b.age.kind === 'bucket'
                                        ? <AgeTag index={b.age.index} of={b.age.of}>{b.age.label}</AgeTag>
                                        : <span className="text-secondary text-label-3">{b.age.kind === 'not_yet_due' ? 'Not yet due' : 'No due date'}</span>}
                                </Td>
                                <Td numeric>
                                    <span className="text-secondary font-semibold text-label">{money.precise(b.pending)}</span>
                                    {(covered.get(b.key) ?? 0) > 0 && (
                                        <span className="block text-caption font-semibold text-accent-text">Timely: {money.exact(covered.get(b.key)!)} covered</span>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
            {more && (
                <div className="px-5 py-2.5 border-t border-separator">
                    <Button variant="quiet" size="sm" aria-expanded={more.all} onClick={more.toggle}>
                        {more.all ? `Show the first ${count(FIRST_BILLS)} only` : `Show all ${count(s.bills.length)} bills`}
                    </Button>
                </div>
            )}
            {/* What the bills add up to, as a statement closes: against the Pending column's edge. */}
            <dl aria-label="What the statement adds up to" className="border-t border-separator px-5 py-3 sm:pl-[40%]">
                <TotalLine label={`${count(s.totals.billCount)} open ${s.totals.billCount === 1 ? 'bill' : 'bills'}`} value={money.precise(s.totals.bills)} />
                {s.unbilledDebit && (
                    <TotalLine label={s.unbilledDebit.reason === 'not_bill_wise' ? STATEMENT_LINE_WORDS.not_bill_wise : `${STATEMENT_LINE_WORDS.owed_on_account}, not aged`} value={money.precise(s.unbilledDebit.amount)} />
                )}
                {s.totals.credits > 0 && <TotalLine label="Less the credits" value={`− ${money.precise(s.totals.credits)}`} />}
                <TotalLine strong label="Balance in TallyPrime" value={`${money.precise(Math.abs(s.totals.balance))} ${side}`} />
            </dl>
            {!s.ties && (
                <p role="note" className="px-5 pb-4 text-secondary text-warn">
                    These lines do not add up to TallyPrime’s balance. They are shown as TallyPrime stated them; Receivables data’s accounting checks say more.
                </p>
            )}
        </div>
    );
}

const TotalLine = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
    <div className={cx('flex items-baseline justify-between gap-4 py-1', strong && 'mt-1 pt-2 border-t border-separator-strong')}>
        <dt className={cx('text-secondary', strong ? 'font-semibold text-label' : 'text-label-2')}>{label}</dt>
        <dd className={cx('text-secondary num whitespace-nowrap', strong ? 'font-semibold text-label' : 'text-label')}>{value}</dd>
    </div>
);

function Credits({ credits, total }: { credits: StatementCredit[]; total: number }) {
    const { money, date } = useFormatters();
    return (
        <>
            <Table caption="Credits, against no bill yet" aria-label="Credits">
                <thead>
                    <tr><Th>Credit</Th><Th hideBelow="sm">Date</Th><Th numeric>Amount</Th></tr>
                </thead>
                <tbody>
                    {credits.map(c => (
                        <tr key={c.key}>
                            <RowHead>
                                <span className="text-secondary font-semibold text-label break-words">{c.ref || STATEMENT_LINE_WORDS[c.kind]}</span>
                                <span className="block text-caption font-normal text-label-3">
                                    {c.kind === 'credit_bill' ? 'A credit note or advance, against no bill yet' : c.kind === 'on_account' ? 'Against no bill; not aged' : 'TallyPrime keeps this ledger without bills'}
                                </span>
                            </RowHead>
                            <Td hideBelow="sm" className="text-secondary text-label-2 num whitespace-nowrap">{c.date ? date.day(c.date) : '—'}</Td>
                            <Td numeric className="text-secondary font-semibold text-label">− {money.precise(c.amount)}</Td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <dl aria-label="What the credits add up to" className="border-t border-separator px-5 py-3 sm:pl-[40%]">
                <TotalLine strong label="Credits" value={`− ${money.precise(total)}`} />
            </dl>
        </>
    );
}

/**
 * The bills TallyPrime no longer lists as open. Receipts are not read from TallyPrime (02 §19.5), so Timely knows a
 * bill closed — on the day of the first update without it — but not how it was paid, and says so.
 */
function Cleared({ cleared }: { cleared: StatementFigures['cleared'] }) {
    const { money, date } = useFormatters();
    return (
        <section aria-labelledby="statement-cleared">
            <h4 id="statement-cleared" className="px-5 pt-4 text-secondary font-semibold text-label">Bills cleared</h4>
            <p className="px-5 pt-0.5 pb-2 text-caption text-label-3 max-w-3xl">
                Bills TallyPrime no longer lists as open, the most recent first, each on the day of the first update that no longer had it open,
                kept for {CLOSED_BILL_MEMORY_DAYS} days. Receipts are not read from TallyPrime, so Timely does not say how a bill was paid.
            </p>
            {cleared.length === 0 ? (
                <p className="px-5 pb-4 text-secondary text-label-3">No bill has cleared since Timely began reading TallyPrime.</p>
            ) : (
                <Table caption="Bills cleared in TallyPrime, the most recent first" aria-label="Cleared bills">
                    <thead>
                        <tr><Th>Bill</Th><Th hideBelow="sm">Bill date</Th><Th>Seen cleared</Th><Th numeric>Was pending</Th></tr>
                    </thead>
                    <tbody>
                        {cleared.map(c => (
                            <tr key={`${c.key}:${c.billDate}`}>
                                <RowHead><span className="text-secondary font-semibold text-label break-words">{c.ref || 'No reference'}</span></RowHead>
                                <Td hideBelow="sm" className="text-secondary text-label-2 num whitespace-nowrap">{date.day(c.billDate)}</Td>
                                <Td className="text-secondary text-label-2 num whitespace-nowrap">{date.day(c.clearedOn)}</Td>
                                <Td numeric className="text-secondary text-label">{money.precise(c.amount)}</Td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </section>
    );
}

/**
 * An account from a receivables sheet: the sheet gives its balance by age, not
 * bill by bill, so its statement is how old the balance is — the balance itself
 * heads the page — and the same printable statement. Its due dates are not read,
 * so nothing here is called overdue (rule 4).
 */
export function SheetStatement({ totalType, bands, statementTo }: {
    totalType: 'Dr' | 'Cr' | undefined;
    bands: readonly (readonly [string, number])[];
    statementTo: string;
}) {
    const { money } = useFormatters();
    const context = useActiveWorkspace();
    return (
        <Panel
            id="account-statement" headingLevel={3} title="Outstanding statement"
            description="From your receivables sheet, which gives this account’s balance by age rather than bill by bill."
            actions={mayExportStatement(context) ? <Link to={statementTo} className={buttonClass({ variant: 'secondary', size: 'sm' })}>Statement PDF</Link> : undefined}
        >
            <div className="px-5 pb-5">
                <h4 className="text-secondary font-semibold text-label">How old the money is</h4>
                <AgeingBar className="mt-3 !h-2.5" parts={bands.map(([, amount]) => amount)} label={`How old the money is:${bands.map(([label, amount]) => `${label} ${money.exact(amount)}`).join(', ')}`} />
                <dl aria-label="How old the money is" className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                    {bands.map(([label, amount], i) => (
                        <div key={label} className="min-w-0">
                            <dt><AgeTag index={i} of={bands.length}>{label}</AgeTag></dt>
                            <dd className={cx('mt-0.5 text-secondary font-semibold num', amount > 0 ? 'text-label' : 'text-label-3')}>{money.exact(amount)}</dd>
                        </div>
                    ))}
                </dl>
                <p className="mt-3 text-caption text-label-3 max-w-3xl">
                    {totalType === 'Cr'
                        ? 'This account is in credit, so nothing on it is owed — whatever the ageing columns say.'
                        : 'An age from the sheet’s ageing columns, not overdue: Timely does not read due dates from a sheet, so overdue is not available.'}
                </p>
            </div>
        </Panel>
    );
}

function EventRow({ event, canAllocate, onAllocate }: { event: StoredEvent; canAllocate: boolean; onAllocate: () => void }) {
    const { money, date } = useFormatters();
    const e = event;
    const explanation = e.detail.explanation ? EXPLANATIONS[e.detail.explanation as AllocationExplanation] : null;
    if (e.kind === 'reversal') {
        return (
            <li className="px-5 py-2.5">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-secondary text-label-2">{date.day(e.effectiveDate)} · pending again</span>
                    <span className="text-secondary num text-label">{money.precise(e.amount)}</span>
                </div>
                <p className="text-caption text-label-3">{(e.detail.bills ?? []).map(b => b.ref).join(', ') || 'A credit'} is pending again in TallyPrime — a receipt cancelled or reversed, or a bill increased.</p>
            </li>
        );
    }
    if (e.kind === 'voucher_changed') {
        return (
            <li className="px-5 py-2.5">
                <span className="text-secondary text-label-2">{date.day(e.effectiveDate)} · changed in TallyPrime</span>
                <p className="text-caption text-label-3">A voucher Timely had read was {String(e.detail.change ?? 'changed').replace(/_/g, ' ')}. The current figures are TallyPrime’s latest.</p>
            </li>
        );
    }
    const status = e.status!;
    const reviewable = canAllocate && (status === 'UNALLOCATED_FROM_TALLY' || status === 'AMBIGUOUS');
    // From bills' movement, not a voucher: the amounts fell, and that is all that is known.
    const what = e.evidence === 'movement' ? 'pending fell' : e.kind === 'allocation_change' ? 'credit applied' : null;
    return (
        <li className="px-5 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-secondary text-label-2 truncate">
                    {date.day(e.effectiveDate)}{e.detail.voucher?.number ? ` · ${e.detail.voucher.type ?? 'Voucher'} ${e.detail.voucher.number}` : ''}
                    {what ? ` · ${what}` : ''}
                </span>
                <span className="text-secondary font-semibold num text-label">{money.precise(e.amount)}</span>
            </div>
            <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                <Badge tone={STATUS_TONE[status]} title={ALLOCATION_STATES[status].meaning}>{ALLOCATION_STATES[status].label}</Badge>
                {e.detail.voucher?.postDated && <Badge tone="neutral">Post-dated</Badge>}
            </div>
            {(e.detail.allocations ?? []).length > 0 && (
                <p className="text-caption text-label-2">{e.detail.allocations!.map(l => `${l.ref} ${money.precise(l.amount)}`).join(', ')}</p>
            )}
            {(e.detail.unallocated ?? []).length > 0 && (
                <p className="text-caption text-label-3">{money.precise(e.detail.unallocated!.reduce((s, u) => s + u.amount, 0))} against no bill in TallyPrime.</p>
            )}
            {explanation && <p className={cx('text-caption max-w-3xl', status === 'AMBIGUOUS' ? 'text-label-2' : 'text-label-3')}>{explanation}</p>}
            {reviewable && <Button size="sm" variant="quiet" onClick={onAllocate}>Review for collections</Button>}
        </li>
    );
}

/**
 * "Allocate for collections": a person says which open bills a credit
 * TallyPrime holds against no bill is for. Checked by the domain as it is
 * typed and by the database when saved; nothing is trimmed to fit.
 */
function AllocationDialog({ workspaceId, customerId, account, existing, event, onClose, onSaved }: {
    workspaceId: string; customerId: string; account: AccountPosition; existing: OperationalAllocation[];
    event: StoredEvent | null; onClose: () => void; onSaved: () => Promise<void>;
}) {
    const { money, date } = useFormatters();
    const [amounts, setAmounts] = useState<Record<string, string>>({});
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // In the bills' own order, whatever order they were typed in.
    const lines = account.bills.filter(b => (amounts[b.key] ?? '').trim() !== '').map(b => ({ billKey: b.key, amount: Number(amounts[b.key]) }));
    const creditEvent = event ? {
        id: event.eventKey, sourceKey: account.sourceKey, evidence: event.evidence, kind: 'credit' as const, effectiveDate: event.effectiveDate, amount: event.amount,
        status: event.status!, allocations: event.detail.allocations ?? [], unallocated: (event.detail.unallocated ?? []) as CreditEvent['unallocated'],
        explanation: (event.detail.explanation ?? 'ambiguous_mixed_movement') as AllocationExplanation,
    } : undefined;
    const problems = validateOperationalAllocation({ sourceKey: account.sourceKey, creditEventId: event?.eventKey ?? null, lines, reason }, { account, existing, ...(creditEvent ? { event: creditEvent } : {}) });
    const total = lines.reduce((s, l) => s + (Number.isFinite(l.amount) ? l.amount : 0), 0);
    const ready = lines.length > 0 && problems.length === 0;

    const save = async () => {
        if (!ready || busy) return;
        setBusy(true); setError(null);
        try { await createAllocation(workspaceId, customerId, event?.eventKey ?? null, lines, reason.trim()); await onSaved(); }
        catch (e) { setError(e instanceof Error ? e.message : 'That could not be saved just now.'); }
        finally { setBusy(false); }
    };

    return (
        <DialogShell
            title="Allocate for collections"
            subtitle={event ? `${money.exact(event.amount)} of ${date.day(event.effectiveDate)}: ${ALLOCATION_STATES[event.status!].label.toLowerCase()}.` : 'Credit TallyPrime holds on this account against no bill.'}
            size="md" onClose={onClose} closeOnEscape={!busy}
            onSubmit={e => { e.preventDefault(); void save(); }}
            footerNote={total > 0 ? `${money.exact(total)} for collections` : undefined}
            footer={<>
                <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={!ready} busy={busy}>Record Timely allocation</Button>
            </>}
        >
            <p className="text-body text-label-2">
                This is a Timely allocation for collections. It does not change TallyPrime, the balance or any bill, and it is never shown as TallyPrime’s.
                If the accountant allocates it in TallyPrime, TallyPrime’s allocation is used and this one is kept for the record.
            </p>
            <fieldset className="mt-4">
                <legend className={labelClass}>Open bills</legend>
                <ul className="grid gap-2">
                    {account.bills.filter(b => b.side === 'Dr').map(b => (
                        <li key={b.key} className="flex items-center justify-between gap-3">
                            <label htmlFor={`alloc-${b.key}`} className="text-secondary text-label-2 min-w-0">
                                <span className="font-semibold text-label">{b.ref}</span> · {date.day(b.billDate)} · {money.exact(b.pending)} pending
                            </label>
                            <input id={`alloc-${b.key}`} inputMode="decimal" placeholder="0" value={amounts[b.key] ?? ''}
                                onChange={e => setAmounts(a => ({ ...a, [b.key]: e.target.value.replace(/[^\d.]/g, '') }))}
                                className={fieldClass({ className: 'w-32 num text-right' })} />
                        </li>
                    ))}
                </ul>
            </fieldset>
            <label className={cx(labelClass, 'mt-4')} htmlFor="alloc-reason">Why</label>
            <textarea id="alloc-reason" rows={2} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} className={fieldClass({ multiline: true, className: 'w-full resize-y' })}
                placeholder="For example: the customer’s remittance advice names these bills." />
            {lines.length > 0 && problems.map(p => <p key={p.code + p.message} className={errorClass}>{p.message}</p>)}
            {error && <p role="alert" className={errorClass}>{error}</p>}
        </DialogShell>
    );
}
